import { fnv1a, mulberry32 } from '../track/rng';
import { MICRO_COUNT } from '../track/generate';
import type { RunResult } from './session';

/**
 * Elimination qualifying for the solo Grand Prix — an F1-style Q1/Q2/Q3
 * knockout run before every stage. The player drives a limited number of
 * flying laps per segment; the AI field's times are *generated* from each
 * driver's pace (the same `pace` value `buildAIRoster` assigns) plus seeded
 * per-segment variance, so a given stage's qualifying is identical for every
 * player and needs no simulation or network.
 *
 * This module is pure: no DOM, no rendering, no timers. `main.ts` owns the
 * screen, the timing board and the reveal-on-a-clock animation; the values
 * here are fixed the moment a segment starts.
 */

export type QualiSegment = 'Q1' | 'Q2' | 'Q3';
export const SEGMENTS: QualiSegment[] = ['Q1', 'Q2', 'Q3'];

/** F1 mini-sector colours: purple session-best, green matched-best, yellow slower. */
export type MicroColor = 'sb' | 'pb' | 'slow' | 'none';

/** Flying laps the player gets per segment; best one counts. */
export const ATTEMPTS_PER_SEGMENT = 3;

interface QualiContext {
  idealLapMs: number;
  /** Ideal share of the lap spent in sectors 1/2/3, summing to 1. */
  sectorFractions: number[];
  /** Fastest speed anywhere on the ideal profile, km/h — the speed-trap ceiling. */
  profileMaxKmh: number;
}

export interface QualiDriver {
  /** `'player'`, or `'ai:<rosterIndex>'` for an AI. */
  id: string;
  name: string;
  isPlayer: boolean;
  /** Pace multiplier — >1 is faster than the ideal reference. */
  pace: number;
  /** Best lap *this segment*, ms — reset when a new segment starts. */
  bestLapMs: number | null;
  /** Most recent completed lap this segment, ms. */
  lastLapMs: number | null;
  /** Cumulative S1/S2/S3 times of the best lap this segment, ms. */
  sectorMs: number[] | null;
  /** Best speed-trap reading this segment, km/h. */
  speedTrapKmh: number | null;
  /** Completed timed laps this segment. */
  laps: number;
  status: 'running' | 'through' | 'out';
  /** Segment the driver was knocked out in, or null if still in / through. */
  outAt: QualiSegment | null;
  /** Best lap the driver held at the moment they were eliminated, for the
   *  final classification (times reset between segments). */
  outLapMs: number | null;
  /** Player only: best per-microsector split across the whole session, ms. */
  bestMicroSplitMs?: number[];
  /** Player only: per-microsector splits of the last completed lap, ms. */
  lastMicroSplitMs?: number[];
  /** Player only: F1 mini-sector colour for each microsector of the last lap. */
  lastMicroColor?: MicroColor[];
}

export interface QualiState {
  stageKey: string;
  seed: number;
  segment: QualiSegment;
  /** Drivers participating in each segment. */
  sizes: Record<QualiSegment, number>;
  drivers: QualiDriver[];
  playerAttemptsLeft: number;
  ctx: QualiContext;
}

const SEGMENT_SALT: Record<QualiSegment, number> = {
  Q1: 0x1111_1111,
  Q2: 0x2222_2222,
  Q3: 0x3333_3333,
};

/**
 * How many drivers take part in each segment. Q1 is the whole field; roughly
 * 45 % of the field survives to Q3, and the drivers eliminated in between are
 * split as evenly as possible between the Q1 and Q2 cuts.
 *
 *  field →  Q1 / Q2 / Q3   (cuts)
 *     6  →   6 /  5 / 3     (1, 2)
 *     8  →   8 /  6 / 4     (2, 2)
 *    12  →  12 /  9 / 5     (3, 4)
 */
export function segmentSizes(fieldSize: number): Record<QualiSegment, number> {
  const q3 = Math.max(2, Math.min(fieldSize - 2, Math.round(fieldSize * 0.45)));
  const totalElim = fieldSize - q3;
  const cut1 = Math.max(1, Math.floor(totalElim / 2));
  const q2 = fieldSize - cut1;
  return { Q1: fieldSize, Q2: q2, Q3: q3 };
}

/** Gaussian-ish noise in roughly N(0, 1), from three uniforms. */
function noise(rng: { next(): number }): number {
  return (rng.next() + rng.next() + rng.next() - 1.5) * 1.4142;
}

export interface GeneratedAiLap {
  lapMs: number;
  /** Cumulative S1/S2/S3, ms. */
  sectorMs: number[];
  speedTrapKmh: number;
}

/**
 * One AI qualifying lap, fully determined by the stage seed, the driver and the
 * segment. Faster `pace` ⇒ faster mean lap; every driver finds a little time as
 * the session moves from Q1 to Q3.
 */
export function generateAiLap(
  driverId: string,
  pace: number,
  segment: QualiSegment,
  ctx: QualiContext,
  seed: number,
): GeneratedAiLap {
  const rng = mulberry32((seed ^ SEGMENT_SALT[segment] ^ fnv1a(driverId)) >>> 0);
  const segIndex = SEGMENTS.indexOf(segment);

  const base = ctx.idealLapMs / pace;
  const improvement = 1 - segIndex * 0.004;
  const lapMs = base * improvement + noise(rng) * base * 0.004;

  let cum = 0;
  const sectorMs = ctx.sectorFractions.map((frac) => {
    cum += lapMs * frac * (1 + noise(rng) * 0.01);
    return cum;
  });
  // Rescale so the last cumulative split is exactly the lap time.
  const scale = lapMs / sectorMs[sectorMs.length - 1];
  for (let i = 0; i < sectorMs.length; i++) sectorMs[i] *= scale;

  const paceFrac = (pace - 0.82) / (1.03 - 0.82);
  const speedTrapKmh =
    ctx.profileMaxKmh * (0.965 + Math.max(0, Math.min(1, paceFrac)) * 0.035) + noise(rng) * 0.6;

  return { lapMs, sectorMs, speedTrapKmh };
}

function makeDriver(id: string, name: string, isPlayer: boolean, pace: number): QualiDriver {
  return {
    id,
    name,
    isPlayer,
    pace,
    bestLapMs: null,
    lastLapMs: null,
    sectorMs: null,
    speedTrapKmh: null,
    laps: 0,
    status: 'running',
    outAt: null,
    outLapMs: null,
  };
}

/** Median of a copy-sorted number list. */
function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
}

/**
 * Build the qualifying session for one stage. `aiPaces` comes straight from
 * `buildAIRoster`; `playerBestMs` is the player's stored best on this exact
 * circuit, used only to seed a plausible player pace (a slow player still
 * qualifies last, exactly as they would on track).
 */
export function createQuali(
  stageKey: string,
  seed: number,
  aiNames: string[],
  aiPaces: number[],
  ctx: QualiContext,
  playerBestMs: number | null,
): QualiState {
  const playerPace =
    playerBestMs && playerBestMs > 0 ? ctx.idealLapMs / playerBestMs : median(aiPaces);

  const drivers: QualiDriver[] = [
    makeDriver('player', 'You', true, playerPace),
    ...aiNames.map((name, i) => makeDriver(`ai:${i}`, name, false, aiPaces[i])),
  ];

  const state: QualiState = {
    stageKey,
    seed,
    segment: 'Q1',
    sizes: segmentSizes(drivers.length),
    drivers,
    playerAttemptsLeft: ATTEMPTS_PER_SEGMENT,
    ctx,
  };
  runAiSegment(state);
  return state;
}

/** Fills every still-running AI driver's numbers for the current segment. */
function runAiSegment(state: QualiState): void {
  for (const d of state.drivers) {
    if (d.isPlayer || d.status !== 'running') continue;
    const lap = generateAiLap(d.id, d.pace, state.segment, state.ctx, state.seed);
    d.bestLapMs = lap.lapMs;
    d.lastLapMs = lap.lapMs;
    d.sectorMs = lap.sectorMs;
    d.speedTrapKmh = lap.speedTrapKmh;
    d.laps = state.segment === 'Q1' ? 2 : 3;
  }
}

/** Per-microsector splits (ms) from a run's cumulative `microMs`. */
export function microSplits(microMs: number[]): number[] {
  const out: number[] = [];
  let prev = 0;
  for (const cum of microMs) {
    out.push(cum - prev);
    prev = cum;
  }
  return out;
}

/**
 * Record one of the player's flying laps. The attempt is always spent; an
 * invalid lap sets no time, matching a deleted qualifying lap.
 */
export function applyPlayerLap(state: QualiState, result: RunResult): void {
  const p = state.drivers.find((d) => d.isPlayer);
  if (!p) return;
  state.playerAttemptsLeft = Math.max(0, state.playerAttemptsLeft - 1);
  if (!result.valid) return;

  p.laps += 1;
  p.lastLapMs = result.timeMs;

  const splits = microSplits(result.microMs);
  p.lastMicroSplitMs = splits;
  const prevBest = p.bestMicroSplitMs;
  // Purple where this lap improved on every previous lap's split for that
  // microsector, green where it merely matched an earlier best, yellow if slower.
  p.lastMicroColor = splits.map((t, i) => {
    if (!prevBest) return 'sb';
    if (t < prevBest[i] - 1e-6) return 'sb';
    if (t <= prevBest[i] + 1e-6) return 'pb';
    return 'slow';
  });
  p.bestMicroSplitMs = prevBest
    ? prevBest.map((best, i) => Math.min(best, splits[i]))
    : [...splits];

  const trap = result.maxSpeed * 3.6;
  p.speedTrapKmh = p.speedTrapKmh === null ? trap : Math.max(p.speedTrapKmh, trap);

  if (p.bestLapMs === null || result.timeMs < p.bestLapMs) {
    p.bestLapMs = result.timeMs;
    p.sectorMs = result.sectorMs.slice(0, 3);
  }
}

/** Drivers taking part in the current segment, best lap first (no time last). */
export function orderedField(state: QualiState): QualiDriver[] {
  return state.drivers
    .filter((d) => d.status === 'running')
    .sort((a, b) => {
      const at = a.bestLapMs ?? Infinity;
      const bt = b.bestLapMs ?? Infinity;
      if (at !== bt) return at - bt;
      return b.pace - a.pace;
    });
}

/** How many drivers the current segment eliminates (0 in Q3). */
export function cutCount(state: QualiState): number {
  if (state.segment === 'Q3') return 0;
  const next = state.segment === 'Q1' ? 'Q2' : 'Q3';
  return state.drivers.filter((d) => d.status === 'running').length - state.sizes[next];
}

/** True once the player can do nothing more in this segment. */
export function segmentExhausted(state: QualiState): boolean {
  return state.playerAttemptsLeft <= 0;
}

/**
 * Close the current segment: knock out the slowest drivers, advance survivors
 * to the next segment with fresh times, and re-run the AI field. In Q3 this
 * just marks everyone still in as `through`.
 */
export function advanceSegment(state: QualiState): void {
  const field = orderedField(state);

  if (state.segment === 'Q3') {
    for (const d of field) d.status = 'through';
    return;
  }

  const next: QualiSegment = state.segment === 'Q1' ? 'Q2' : 'Q3';
  const keep = state.sizes[next];
  field.forEach((d, i) => {
    if (i < keep) {
      // Fresh segment — times reset, learned microsectors carry over.
      d.bestLapMs = null;
      d.lastLapMs = null;
      d.sectorMs = null;
      d.laps = 0;
    } else {
      d.status = 'out';
      d.outAt = state.segment;
      d.outLapMs = d.bestLapMs;
    }
  });

  state.segment = next;
  state.playerAttemptsLeft = ATTEMPTS_PER_SEGMENT;
  runAiSegment(state);
}

/** True once Q3 has been closed. */
export function isQualiComplete(state: QualiState): boolean {
  return state.segment === 'Q3' && state.drivers.every((d) => d.status !== 'running');
}

/**
 * Final grid order, pole first: Q3 order on top (by their Q3 time), then the
 * Q2 casualties by their Q2 time, then the Q1 casualties by their Q1 time.
 */
export function classification(state: QualiState): QualiDriver[] {
  const byTime = (a: QualiDriver, b: QualiDriver, ta: number | null, tb: number | null): number => {
    const x = ta ?? Infinity;
    const y = tb ?? Infinity;
    if (x !== y) return x - y;
    return b.pace - a.pace;
  };
  const through = state.drivers
    .filter((d) => d.outAt === null)
    .sort((a, b) => byTime(a, b, a.bestLapMs, b.bestLapMs));
  const q2out = state.drivers
    .filter((d) => d.outAt === 'Q2')
    .sort((a, b) => byTime(a, b, a.outLapMs, b.outLapMs));
  const q1out = state.drivers
    .filter((d) => d.outAt === 'Q1')
    .sort((a, b) => byTime(a, b, a.outLapMs, b.outLapMs));
  return [...through, ...q2out, ...q1out];
}

/** 1-based grid position of the player in the final classification. */
export function playerQualiPosition(state: QualiState): number {
  return classification(state).findIndex((d) => d.isPlayer) + 1;
}

/** The best lap the player could string together from their best microsectors. */
export function theoreticalBestMs(driver: QualiDriver): number | null {
  if (!driver.bestMicroSplitMs || driver.bestMicroSplitMs.length === 0) return null;
  return driver.bestMicroSplitMs.reduce((a, b) => a + b, 0);
}

/** F1 mini-sector colour for the player's last lap, one entry per microsector. */
export function microStripColors(driver: QualiDriver): MicroColor[] {
  return driver.lastMicroColor ?? new Array(MICRO_COUNT).fill('none');
}

/**
 * Maps a final classification to the `{ playerRow, aiRows }` shape
 * `buildAIGrid`'s `gridOrder` option expects. `aiRows[i]` is the grid row for
 * roster racer `i` (0 = pole).
 */
export function gridRowsFor(
  ordered: QualiDriver[],
  aiCount: number,
): { playerRow: number; aiRows: number[] } {
  const aiRows = new Array<number>(aiCount).fill(aiCount);
  let playerRow = ordered.length - 1;
  ordered.forEach((d, row) => {
    if (d.isPlayer) {
      playerRow = row;
      return;
    }
    const idx = Number(d.id.slice(3));
    if (Number.isInteger(idx) && idx >= 0 && idx < aiCount) aiRows[idx] = row;
  });
  return { playerRow, aiRows };
}
